/**
 * UpdateOverlay — global, always-mounted in App.tsx, above all routes.
 *
 * Two display modes:
 *  - Full-screen overlay when an update is ready and about to apply
 *    (reloadsThisSession > 0 indicates the watcher just kicked off a reload;
 *     the overlay then covers the screen until the new bundle takes over).
 *  - Discreet pill when an update is available but deferred because an
 *    exam/quiz/practice session is active.
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { versionWatcher, type WatcherState } from "@/lib/version-watcher";
import { Loader2 } from "lucide-react";

export const UpdateOverlay = () => {
  const [state, setState] = useState<WatcherState>(() => versionWatcher.getState());
  const location = useLocation();

  useEffect(() => {
    return versionWatcher.subscribe(setState);
  }, []);

  if (state.disabled) return null;

  const reloading = state.reloadsThisSession > 0;

  if (reloading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="fixed inset-0 z-[2147483647] flex flex-col items-center justify-center gap-4 bg-background text-foreground px-6 text-center"
      >
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <div className="text-lg font-semibold">Actualizando MBLearn…</div>
        <div className="max-w-xs text-sm opacity-75 leading-relaxed">
          Estamos preparando la nueva versión. No cierres esta ventana.
        </div>
      </div>
    );
  }

  if (state.pendingReload && state.examActive && location.pathname !== "/") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed left-1/2 top-3 z-[2147483646] -translate-x-1/2 rounded-full border border-border bg-card/95 px-3 py-1.5 text-xs text-muted-foreground shadow backdrop-blur"
      >
        Actualización disponible. Se aplicará al terminar.
      </div>
    );
  }

  return null;
};

export default UpdateOverlay;
